import { layoutFor } from '../lib/layout';
import { atlas } from './atlas';
import type { AtlasPhase, AtlasSite, Confidence, GraphNode, GraphRelation } from './types';

const sourceId = atlas.sourceId;

const knownOrganizationNodes: Record<string, string> = {
  bewelana: 'company-bewelana',
};

function slug(value: string): string {
  return value.toLocaleLowerCase('pl-PL').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function atlasSiteNodeId(siteId: string): string {
  return `atlas-site-${siteId}`;
}

function organizationKey(phase: AtlasPhase): string {
  return phase.organizationId === 'private' ? `private-${slug(phase.label)}` : phase.organizationId;
}

function organizationNodeId(phase: AtlasPhase): string {
  return knownOrganizationNodes[phase.organizationId] ?? `atlas-org-${organizationKey(phase)}`;
}

function siteConfidence(site: AtlasSite): Confidence {
  if (site.locationConfidence === 'confirmed') return 'high';
  if (site.locationConfidence === 'approximate') return 'medium';
  return 'low';
}

function yearRange(site: AtlasSite): { start?: string; end?: string } {
  if (site.phases.length === 0) return {};
  return {
    start: String(Math.min(...site.phases.map((phase) => phase.startYear))),
    end: String(Math.max(...site.phases.map((phase) => phase.endYear))),
  };
}

const organizationPhases = new Map<string, AtlasPhase[]>();
for (const site of atlas.sites) {
  for (const phase of site.phases) {
    const id = organizationNodeId(phase);
    if (knownOrganizationNodes[phase.organizationId]) continue;
    organizationPhases.set(id, [...(organizationPhases.get(id) ?? []), phase]);
  }
}

const siteNodes: GraphNode[] = atlas.sites.map((site, index) => ({
  id: atlasSiteNodeId(site.id),
  type: 'place',
  title: `${site.sourceOrdinal} · ${site.title}`,
  subtitle: site.currentAddress,
  aliases: [site.historicAddress, site.currentAddress, ...site.phases.map((phase) => phase.label)],
  summary: site.summary,
  curatorialNote: `${site.locationNote}${site.presentUse ? ` Współczesna funkcja: ${site.presentUse}.` : ''}`,
  dateRange: yearRange(site),
  place: `${site.municipality}, ${site.currentAddress}`,
  confidence: siteConfidence(site),
  confidenceNote: site.locationNote,
  tags: ['atlas ZPW', 'miejsce przemysłowe', site.presentStatus, site.locationConfidence, ...new Set(site.phases.map((phase) => phase.organizationLabel))],
  sourceIds: site.sourceIds,
  layout: layoutFor(atlasSiteNodeId(site.id), 'place', 100 + index),
  featured: false,
}));

const organizationNodes: GraphNode[] = [...organizationPhases.entries()].map(([id, phases], index) => {
  const labels = [...new Set(phases.map((phase) => phase.organizationLabel))];
  const phaseLabels = [...new Set(phases.map((phase) => phase.label))];
  const start = Math.min(...phases.map((phase) => phase.startYear));
  const end = Math.max(...phases.map((phase) => phase.endYear));
  const confidence: Confidence = phases.some((phase) => phase.confidence === 'low') ? 'low' : phases.some((phase) => phase.confidence === 'medium') ? 'medium' : 'high';
  const isPrivate = phases[0].organizationId === 'private';
  return {
    id,
    type: 'company',
    title: isPrivate ? phaseLabels[0] : (labels[0] ?? phaseLabels[0]),
    subtitle: isPrivate ? 'przedsiębiorstwo prywatne' : 'organizacja przemysłu włókienniczego',
    aliases: phaseLabels.filter((label) => !labels.includes(label)),
    summary: `Organizacja występująca w ${phases.length} ${phases.length === 1 ? 'fazie' : 'fazach'} historii miejsc zestawienia ZPW_PRL.`,
    curatorialNote: 'Węzeł został utworzony z faz organizacyjnych Atlasu; zakres dat opisuje okres obecny w zestawieniu, niekoniecznie pełny czas działania organizacji.',
    dateRange: { start: String(start), end: String(end) },
    place: 'Bielsko-Biała i region',
    confidence,
    confidenceNote: 'Daty i nazwy pochodzą z opracowania ZPW_PRL Piotra Keniga.',
    tags: ['atlas ZPW', isPrivate ? 'przedsiębiorstwo prywatne' : 'PRL', ...phaseLabels],
    sourceIds: [sourceId],
    layout: layoutFor(id, 'company', 160 + index),
  };
});

const phaseRelations: GraphRelation[] = atlas.sites.flatMap((site) => site.phases.map((phase) => ({
  id: `atlas-phase-${site.id}-${phase.id}`,
  from: atlasSiteNodeId(site.id),
  to: organizationNodeId(phase),
  type: 'part-of',
  label: phase.plantCode ? `należał do · zakład ${phase.plantCode}` : 'należał do',
  description: phase.note ?? `${site.title} w tej fazie należał do organizacji ${phase.organizationLabel}.`,
  dateRange: { start: String(phase.startYear), end: String(phase.endYear) },
  confidence: phase.confidence,
  sourceIds: phase.sourceIds,
  significance: 'core',
  directed: true,
})));

const bridgeRelations: GraphRelation[] = atlas.sites.flatMap((site) => site.graphNodeIds.map((graphNodeId, index) => ({
  id: `atlas-bridge-${site.id}-${index}`,
  from: atlasSiteNodeId(site.id),
  to: graphNodeId,
  type: 'connects',
  label: 'powiązany rekord',
  description: 'Most między rekordem miejsca ZPW_PRL a wcześniej opracowanym węzłem kuratorskiego grafu.',
  confidence: 'high',
  sourceIds: site.sourceIds,
  significance: 'contextual',
})));

export const atlasGraphNodes = [...siteNodes, ...organizationNodes];
export const atlasGraphRelations = [...phaseRelations, ...bridgeRelations];
